import { RGBA } from '@ctrl/tinycolor'
import React, { useCallback, useEffect, useMemo } from 'react'

export interface ColorPickerAlphaProps {
  rgb: RGBA
  onChange: (opacity: number) => void
}

export const ColorPickerAlpha = ({ rgb, onChange }: ColorPickerAlphaProps) => {
  const sliderRef = React.useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = React.useState(false)

  const calculateAlpha = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!sliderRef.current) {
        return rgb.a ?? 1
      }

      const rect = sliderRef.current.getBoundingClientRect()
      let clientX: number

      if ('touches' in e && e.touches.length > 0) {
        clientX = e.touches[0].clientX
      } else if ('clientX' in e) {
        clientX = e.clientX
      } else {
        return rgb.a ?? 1
      }

      const left = clientX - rect.left
      const alpha = Math.max(0, Math.min(1, left / rect.width))

      return alpha
    },
    [rgb.a],
  )

  const handleAlphaChange = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      onChange(calculateAlpha(e))
    },
    [onChange, calculateAlpha],
  )

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    handleAlphaChange(e)
  }

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging) {
        handleAlphaChange(e)
      }
    },
    [isDragging, handleAlphaChange],
  )

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true)
    handleAlphaChange(e)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault()
    if (isDragging) {
      handleAlphaChange(e)
    }
  }

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseUp])

  const clampLeft = useMemo(() => `${rgb.a * 100}%`, [rgb.a])

  return (
    <div
      role="spinbutton"
      className="relative h-4"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseDown={handleMouseDown}
    >
      <div
        role="spinbutton"
        className="relative top-1 h-2 w-full cursor-pointer overflow-hidden rounded-full"
        ref={sliderRef}
        onMouseDown={handleMouseDown}
      >
        <div
          className={
            "absolute h-2 w-full bg-[url('../src/assets/images/checkers.png')] bg-contain opacity-10"
          }
        />
        <div
          className="absolute h-2 w-full"
          // tailwind will not work with bg-gradient-to-r from-[rgb(${rgb.r},${rgb.g},${rgb.b})] from-0% to-[rgb(${rgb.r},${rgb.g},${rgb.b})] to-100%]
          style={{
            background: `linear-gradient(to right, rgba(${rgb.r},${rgb.g},${rgb.b}, 0) 0%,  rgba(${rgb.r},${rgb.g},${rgb.b}, 1) 100%)`,
          }}
        />
      </div>
      <button
        type="button"
        className="-translate-x-[50%] absolute top-0.5 cursor-grab rounded-full border-2 border-bgPrimaryBase bg-transparent p-1 shadow-md"
        style={{
          left: clampLeft,
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
      />
    </div>
  )
}
